import { SettingsPage } from "client/Features/Settings/SettingsPage";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	fetchWebDavModels,
	save,
	WebDavOpenDocumentSettingsSaveModel,
	WebDavServerModel,
	WebDavSettingsModel,
	WebDavSettingsSaveModel,
} from "client/Api/WebDavSettings/webDavSettings";
import {
	getClientId,
	hasClientId,
	setClientId
} from "client/Misc/clientId";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { EMPTY_ARRAY } from "client/Misc/utils";
import {
	Form,
	Tabs,
	ToolbarAction
} from "client/Components";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";
import { OfficeDocumentEditor } from "client/Api/Documents/common";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl,
	IValidationContext
} from "client/Validation/rules";
import { useSidebarMenuStore } from "../SidebarMenu/sidebarMenuStore";
import { FetchCallback } from "client/zustand/createAutoFetchStore";
import { WebDavServersTab } from "./WebDavServersTab";
import { WebDavOpenDocumentsSettingsTab } from "./WebDavOpenDocumentsSettingsTab";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { createIpAddressesValidation } from "client/Features/ConditionalAccess/IpAddresses";

export const WebDavSettings = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [formData, setFormData] = useState<WebDavSettingsSaveModel>(defaultFormData);
	const [pageData, setPageData] = useState<PageData>(defaultPageData);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	useActiveSettingsMenuItem(SettingsPageKind.FileServers);

	const formHandler: IFormHandler<WebDavSettingsSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	const initializeSidebar: FetchCallback = useSidebarMenuStore(x => x.fetch);

	useEffect(
		(): (() => void) => {
			const fetchModels = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);

					const models: WebDavSettingsModel = await fetchWebDavModels(signal)
						.catch(handlePageDataLoadingError);

					setFormData(
						mapToWebDavSettingsSaveModel(models)
					);

					setPageData({
						openOnlineConfigured: models.openDocumentSettings
							.openOnlineConfigured,
						usersAndUserGroupsData: models.openDocumentSettings
							.openLocalPermissions
							.usersAndUserGroupsData
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			fetchModels(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				const model: WebDavSettingsSaveModel = prepareForSaving(formData);
				const { webDavServers } = await save(model, globalCancellation);
				setFormData(x => ({ ...x, webDavServers }));

				alertManager.showSuccess(localize("_ChangesSaved"));
				initializeSidebar({ forceReload: true });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			initializeSidebar,
			isBusy
		]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => [
			{
				kind: ActionKind.Regular,
				name: "submit",
				title: localize("_Save"),
				canExecute: () => true,
				execute: submitHandler,
				icon: "floppy-disk",
				isPrimary: true
			},
			{
				kind: ActionKind.Regular,
				name: "cancel",
				title: localize("_Cancel"),
				canExecute: () => true,
				execute: () => {
					window.location.reload();
				},
				icon: "xmark",
			}
		],
		[submitHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions
				},
				false,
				isResponsive,
				false
			);
		},
		[defaultActions, isResponsive]
	);

	const actionContext = undefined;

	return (
		<SettingsPage
			actions={topBarActions}
			actionContext={actionContext}
			hasBusyIndicator={isBusy}
			title={localize("_FileServerDocuments")}
		>
			<Form onSubmit={submitHandler}>
				<Tabs>
					<Tabs.Tab
						{...formHandler
							.under("webDavServers")
							.attachToTab()}
						id="file-servers"
						title={localize("_FileServers")}
					>
						<WebDavServersTab serversAttachable={formHandler.for("webDavServers")} />
					</Tabs.Tab>
					<Tabs.Tab
						{...formHandler
							.under("openDocumentSettings")
							.attachToTab()}
						id="settings"
						title={localize("_Settings")}
					>
						<WebDavOpenDocumentsSettingsTab
							settingsAttachable={formHandler.for("openDocumentSettings")}
							usersAndUserGroupsData={pageData.usersAndUserGroupsData}
							openOnlineConfigured={pageData.openOnlineConfigured}
						/>
					</Tabs.Tab>
				</Tabs>
			</Form>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};

WebDavSettings.displayName = "WebDavSettings";

const defaultFormData: WebDavSettingsSaveModel = {
	webDavServers: [],
	openDocumentSettings: {
		openLocalEnabled: false,
		openOnlineEnabled: false,
		defaultOfficeDocumentEditor: OfficeDocumentEditor.Local,
		onlineEditorsOpenTarget: OfficeWebAppOpenTarget.NewOffice365Tab,
		openLocalConditionalAccessSettings: {
			restrictIp: false,
			whitelistIpRanges: [],
			blacklistIpRanges: []
		},
		openLocalPermissions: {
			everyoneHasAccess: true,
			userIds: [],
			userGroupIds: []
		}
	}
};

function mapToWebDavSettingsSaveModel(
	model: WebDavSettingsModel
): WebDavSettingsSaveModel {
	const {
		openLocalConditionalAccessSettings,
		defaultOfficeDocumentEditor,
		openLocalPermissions,
		onlineEditorsOpenTarget,
		openOnlineEnabled,
		openLocalEnabled,
	} = model.openDocumentSettings;

	const { blacklistIpRanges, whitelistIpRanges } =
		openLocalConditionalAccessSettings;

	return {
		webDavServers: model.webDavServers.map((server) => ({ ...server })),
		openDocumentSettings: {
			openLocalConditionalAccessSettings: {
				restrictIp:
					Boolean(blacklistIpRanges.length) ||
					Boolean(whitelistIpRanges.length),
				blacklistIpRanges: blacklistIpRanges.map(value => setClientId({ value })),
				whitelistIpRanges: whitelistIpRanges.map(value => setClientId({ value })),
			},
			defaultOfficeDocumentEditor,
			openLocalPermissions: {
				everyoneHasAccess: openLocalPermissions.everyoneHasAccess,
				userGroupIds: [...openLocalPermissions.userGroupIds],
				userIds: [...openLocalPermissions.userIds],
			},
			onlineEditorsOpenTarget,
			openOnlineEnabled,
			openLocalEnabled,
		},
	};
}

function prepareForSaving(input: WebDavSettingsSaveModel): WebDavSettingsSaveModel {
	let {
		openDocumentSettings: {
			openLocalEnabled,
			openLocalConditionalAccessSettings,
			openLocalPermissions
		}
	} = input;

	if (!openLocalEnabled) {
		openLocalConditionalAccessSettings = {
			...openLocalConditionalAccessSettings,
			restrictIp: false
		};
	}

	if (!openLocalConditionalAccessSettings.restrictIp) {
		openLocalConditionalAccessSettings = {
			...openLocalConditionalAccessSettings,
			blacklistIpRanges: [],
			whitelistIpRanges: []
		};
	}

	if (openLocalPermissions.everyoneHasAccess) {
		openLocalPermissions = {
			...openLocalPermissions,
			userIds: [],
			userGroupIds: []
		};
	}

	return {
		...input,
		openDocumentSettings: {
			...input.openDocumentSettings,
			openLocalConditionalAccessSettings,
			openLocalPermissions
		}
	};
}

const defaultPageData: PageData = {
	usersAndUserGroupsData: {
		users: EMPTY_ARRAY,
		userGroups: EMPTY_ARRAY
	},
	openOnlineConfigured: false
};

type PageData = {
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	openOnlineConfigured: boolean;
};

const validationBuilder: IValidationConfiguration<WebDavSettingsSaveModel> =
	createValidationConfiguration<WebDavSettingsSaveModel>();

validationBuilder
	.forObject("openDocumentSettings")
	.forScalar("openLocalEnabled")
	.add(
		inline((value: boolean, context: IValidationContext): InputValidationResult => {
			const parentValue: WebDavOpenDocumentSettingsSaveModel = context.parentValue as WebDavOpenDocumentSettingsSaveModel;
			const openOnlineEnabled: boolean = parentValue.openOnlineEnabled;
			const openLocalEnabled: boolean = value;

			if (!openOnlineEnabled && !openLocalEnabled) {
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_AtLeastOneMethodShouldBeSelected")
				};
			}

			return {
				kind: InputValidationResultKind.Valid
			};
		})
			.fixErrorOn("anyInput")
			.validateOn("anyInput")
	);

validationBuilder
	.forObject("openDocumentSettings")
	.when(model => model.openLocalEnabled)
	.forObject("openLocalConditionalAccessSettings")
	.addValidators(createIpAddressesValidation());

const fileServersValidation = validationBuilder
	.forCollection("webDavServers")
	.forItems(getValidationIdForWebServer);

fileServersValidation
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

fileServersValidation
	.forScalar("url")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		})
	);

function getValidationIdForWebServer(item: WebDavServerModel): string {
	if (item.id) {
		return item.id.toString();
	}

	if (hasClientId(item)) {
		return getClientId(item);
	}

	throw Error("Client id is not set on the WebDavServerModel object");
}

export const webDavSettingsRouting = {
	page: "/WebDavSettings"
};
